import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Copy, Download, Upload, X } from "lucide-react";
import { adminApi, apiError, STAGE_LABELS } from "../adminApi";
import { Panel, Pill, inputClass, buttonClass } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";
import { formatStamp } from "../../lib/harvestDates";

const TIER_LABEL = { 1: "Tier 1", 2: "Tier 2", 3: "Tier 3", rel: "Partner" };
const IMPORT_COLUMNS = ["company", "account_id", "tier", "package_key", "priority_score",
  "slug", "contact_name", "contact_email", "peo_user"];

const parseCsv = (text) => {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    const row = {};
    header.forEach((key, i) => {
      if (!IMPORT_COLUMNS.includes(key)) return;
      const value = (cells[i] || "").trim();
      if (!value) return;
      row[key] = key === "priority_score" ? Number(value) : value;
    });
    return row;
  });
};

const AccountDrawer = ({ accountId, landingBase, bookingUrl, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState({ png: "", svg: "" });

  useEffect(() => {
    if (!accountId) return;
    adminApi.get(`/crm/harvest/accounts/${accountId}/timeline`)
      .then(({ data }) => setDetail(data))
      .catch((error) => toast.error(apiError(error)));
  }, [accountId]);

  const scanUrl = detail?.account?.slug
    ? `https://ohcbenefits.com/h/${detail.account.slug}`
    : "";

  useEffect(() => {
    if (!scanUrl) return;
    Promise.all([
      QRCode.toDataURL(scanUrl, { margin: 1, width: 512 }),
      QRCode.toString(scanUrl, { type: "svg", margin: 1 }),
    ]).then(([png, svg]) => setQr({ png, svg })).catch(() => setQr({ png: "", svg: "" }));
  }, [scanUrl]);

  const download = (href, filename) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.click();
  };

  if (!accountId) return null;
  const account = detail?.account;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" data-testid="harvest-account-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto border-l border-cream/10 bg-[#10211A] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
              {account ? TIER_LABEL[account.tier] : "Loading"}
            </p>
            <h3 className="mt-2 font-serif text-2xl text-cream">
              {detail?.account?.company || account?.contact_name || accountId}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="harvest-drawer-close"
            className="rounded-full border border-cream/15 p-1.5 text-cream/50 hover:text-cream"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {account ? (
          <div className="mt-6 space-y-6">
            <div className="rounded-lg border border-cream/10 bg-cream/[0.03] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                Harvest Plan link
              </p>
              <p className="mt-2 break-all font-mono text-xs text-cream/80" data-testid="harvest-drawer-slug">
                {scanUrl}
              </p>
              <p className="mt-1 text-xs text-cream/40">Redirects to {landingBase}?c={account.slug}</p>
              {qr.png ? (
                <div className="mt-4 flex items-center gap-4">
                  <img src={qr.png} alt="QR code" className="h-24 w-24 rounded bg-cream p-1" />
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => download(qr.png, `harvest-${account.slug}.png`)}
                      data-testid="harvest-qr-png"
                      className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-3 py-1.5 text-xs text-cream/70 hover:text-cream"
                    >
                      <Download className="h-3.5 w-3.5" /> PNG
                    </button>
                    <button
                      type="button"
                      onClick={() => download(
                        `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr.svg)}`,
                        `harvest-${account.slug}.svg`)}
                      data-testid="harvest-qr-svg"
                      className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-3 py-1.5 text-xs text-cream/70 hover:text-cream"
                    >
                      <Download className="h-3.5 w-3.5" /> SVG
                    </button>
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(bookingUrl);
                  toast.success("Booking link copied");
                }}
                data-testid="harvest-drawer-booking-copy"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"
              >
                <Copy className="h-3 w-3" /> Book a 15-minute review
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[["Scans", account.scans || 0], ["Last scan", formatStamp(account.last_scan_at)],
                ["Gift total", `$${(account.gift_total || 0).toFixed(2)}`],
                ["Renewal month", account.renewal_month || "—"]].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-cream/10 px-4 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">{label}</p>
                  <p className="mt-1.5 text-sm text-cream/90">{value}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">
                Touch timeline
              </p>
              <div className="mt-3 space-y-2" data-testid="harvest-drawer-timeline">
                {[
                  ...(detail.shipments || []).map((s) => ({
                    ts: s.delivered_at || s.shipped_at || s.created_at,
                    text: `${s.package_key} · ${s.status}${s.tracking_no ? ` · ${s.tracking_no}` : ""}`,
                  })),
                  ...(detail.scans || []).map((s) => ({ ts: s.ts, text: `QR scan · ${s.source}` })),
                  ...(detail.activities || []).map((a) => ({
                    ts: a.created_at, text: `${a.type} · ${(a.body || "").slice(0, 80)}`,
                  })),
                ]
                  .sort((a, b) => String(b.ts).localeCompare(String(a.ts)))
                  .slice(0, 25)
                  .map((row, i) => (
                    <div key={i} className="flex gap-3 border-b border-cream/5 pb-2 text-xs">
                      <span className="w-28 shrink-0 font-mono text-[10px] text-cream/35">
                        {formatStamp(row.ts)}
                      </span>
                      <span className="text-cream/70">{row.text}</span>
                    </div>
                  ))}
                {!detail.shipments?.length && !detail.scans?.length && !detail.activities?.length ? (
                  <p className="text-sm text-cream/40">Nothing logged yet.</p>
                ) : null}
              </div>
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">Gift log</p>
              <div className="mt-3 space-y-2" data-testid="harvest-drawer-gift-log">
                {(detail.gift_log || []).map((gift) => (
                  <div key={gift.id} className="flex justify-between text-xs text-cream/70">
                    <span>{gift.items?.map((i) => i.name).join(", ") || "—"}</span>
                    <span className="font-mono">${gift.retail_value?.toFixed(2)}</span>
                  </div>
                ))}
                {!detail.gift_log?.length ? (
                  <p className="text-sm text-cream/40">No gifts recorded.</p>
                ) : null}
              </div>
            </div>

            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Notes</span>
              <textarea
                defaultValue={account.notes || ""}
                onBlur={async (e) => {
                  try {
                    await adminApi.patch(`/crm/harvest/accounts/${accountId}`, { notes: e.target.value });
                    toast.success("Notes saved");
                    onChanged();
                  } catch (error) { toast.error(apiError(error)); }
                }}
                rows={3}
                data-testid="harvest-drawer-notes"
                className={`${inputClass} mt-1.5`}
              />
            </label>
          </div>
        ) : (
          <p className="mt-8 text-sm text-cream/40">Loading…</p>
        )}
      </div>
    </div>
  );
};

export default function AdminHarvestAccounts() {
  const { settings, pending } = useHarvestHeader();
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filters, setFilters] = useState({ tier: "", stage: "", package_key: "",
    scanned_7d: false, has_renewal: false, peo_user: "" });
  const [callList, setCallList] = useState(false);
  const [open, setOpen] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importRows, setImportRows] = useState([]);

  const load = () => {
    const params = {};
    if (filters.tier) params.tier = filters.tier;
    if (filters.stage) params.stage = filters.stage;
    if (filters.package_key) params.package_key = filters.package_key;
    if (filters.scanned_7d) params.scanned_7d = true;
    if (filters.has_renewal) params.has_renewal = true;
    if (filters.peo_user) params.peo_user = filters.peo_user === "yes";
    adminApi.get("/crm/harvest/accounts", { params })
      .then(({ data }) => setRows(data.accounts))
      .catch((error) => toast.error(apiError(error)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);
  useEffect(() => {
    adminApi.get("/crm/harvest/cadences").then(({ data }) => setPackages(data.packages)).catch(() => {});
  }, []);

  const ranked = useMemo(() => {
    if (!callList) return rows;
    const dayMs = 86400000;
    const rank = (row) => {
      const scanAge = row.last_scan_at ? (Date.now() - new Date(row.last_scan_at)) / dayMs : 999;
      if (scanAge < 1) return 0;
      if (row.harvest_status === "replied") return 1;
      if (row.harvest_status === "delivered") return 2;
      if (row.next_step?.due && new Date(row.next_step.due) <= new Date()) return 3;
      return 4;
    };
    return [...rows].sort((a, b) => rank(a) - rank(b) || b.priority_score - a.priority_score);
  }, [rows, callList]);

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const parsed = parseCsv(await file.text());
    if (!parsed.length) { toast.error("No rows found in that file"); return; }
    setImportRows(parsed);
    try {
      const { data } = await adminApi.post("/crm/harvest/accounts/import",
        { rows: parsed, dry_run: true });
      setImportPreview(data);
    } catch (error) { toast.error(apiError(error)); }
  };

  const commitImport = async () => {
    try {
      const { data } = await adminApi.post("/crm/harvest/accounts/import",
        { rows: importRows, dry_run: false });
      toast.success(`${data.created} added, ${data.updated} updated`);
      setImportPreview(null);
      setImportRows([]);
      load();
    } catch (error) { toast.error(apiError(error)); }
  };

  return (
    <HarvestShell settings={settings} pending={pending}>
      <Panel
        title={`Season accounts · ${rows.length}`}
        testid="harvest-accounts-panel"
        action={
          <div className="flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-cream/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 hover:text-cream">
              <Upload className="h-3.5 w-3.5" /> Import CSV
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden"
                data-testid="harvest-import-input" />
            </label>
            <button
              type="button"
              onClick={() => setCallList((v) => !v)}
              data-testid="harvest-call-list-toggle"
              className={`rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                callList ? "bg-gold/20 text-gold" : "border border-cream/15 text-cream/60"
              }`}
            >
              Call list
            </button>
          </div>
        }
      >
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <select value={filters.tier} onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            data-testid="harvest-accounts-filter-tier" className={inputClass}>
            <option value="">All tiers</option>
            {Object.entries(TIER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.stage} onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
            data-testid="harvest-accounts-filter-stage" className={inputClass}>
            <option value="">All stages</option>
            {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.package_key}
            onChange={(e) => setFilters({ ...filters, package_key: e.target.value })}
            data-testid="harvest-accounts-filter-package" className={inputClass}>
            <option value="">All packages</option>
            {packages.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <select value={filters.peo_user}
            onChange={(e) => setFilters({ ...filters, peo_user: e.target.value })}
            data-testid="harvest-accounts-filter-peo" className={inputClass}>
            <option value="">PEO: any</option>
            <option value="yes">PEO user</option>
            <option value="no">Not on a PEO</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-cream/60">
            <input type="checkbox" checked={filters.scanned_7d}
              onChange={(e) => setFilters({ ...filters, scanned_7d: e.target.checked })}
              data-testid="harvest-accounts-filter-scanned" className="h-4 w-4 accent-[#C8A24A]" />
            Scanned in 7 days
          </label>
          <label className="flex items-center gap-2 text-xs text-cream/60">
            <input type="checkbox" checked={filters.has_renewal}
              onChange={(e) => setFilters({ ...filters, has_renewal: e.target.checked })}
              data-testid="harvest-accounts-filter-renewal" className="h-4 w-4 accent-[#C8A24A]" />
            Has renewal month
          </label>
        </div>

        {importPreview ? (
          <div className="mt-5 rounded-lg border border-gold/30 bg-gold/5 p-4" data-testid="harvest-import-preview">
            <p className="text-sm text-cream">
              {importPreview.rows} rows parsed. {importPreview.preview.filter((r) => r.action === "create account").length} new
              accounts, {importPreview.preview.filter((r) => r.action !== "create account").length} matched existing.
            </p>
            <div className="mt-3 max-h-48 overflow-y-auto text-xs text-cream/60">
              {importPreview.preview.map((row, i) => (
                <div key={i} className="flex justify-between border-b border-cream/5 py-1">
                  <span>{row.company} · {TIER_LABEL[row.tier]}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/40">
                    {row.action}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-3">
              <button type="button" onClick={commitImport} className={buttonClass}
                data-testid="harvest-import-commit">
                Import {importPreview.rows} rows
              </button>
              <button type="button" onClick={() => setImportPreview(null)}
                className="text-xs text-cream/50 hover:text-cream" data-testid="harvest-import-cancel">
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[1100px] text-left text-sm" data-testid="harvest-accounts-table">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                {["Company", "Tier", "Package", "Score", "Stage", "Harvest status", "Renewal",
                  "PEO", "Next step", "Scans", "Last scan", "Gifts"].map((h) => (
                  <th key={h} className="pb-3 pr-4 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ranked.map((row) => (
                <tr
                  key={row.account_id}
                  onClick={() => setOpen(row.account_id)}
                  data-testid={`harvest-account-row-${row.account_id}`}
                  className="cursor-pointer border-t border-cream/5 transition-colors duration-200 hover:bg-cream/5"
                >
                  <td className="py-3 pr-4 text-cream">{row.company}</td>
                  <td className="py-3 pr-4"><Pill>{TIER_LABEL[row.tier]}</Pill></td>
                  <td className="py-3 pr-4 text-cream/60">{row.package_key || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">{row.priority_score}</td>
                  <td className="py-3 pr-4 text-cream/60">{STAGE_LABELS[row.stage] || row.stage}</td>
                  <td className="py-3 pr-4 text-cream/60">{row.harvest_status}</td>
                  <td className="py-3 pr-4 text-cream/60">
                    {row.renewal_month || row.crm_renewal_month || "—"}
                  </td>
                  <td className="py-3 pr-4 text-cream/60">{row.peo_user ? row.peo_name || "Yes" : "—"}</td>
                  <td className="py-3 pr-4 text-cream/60">
                    {row.next_step?.step_id ? `${row.next_step.step_id} · ${row.next_step.due}` : "—"}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">{row.scans || 0}</td>
                  <td className="py-3 pr-4 text-xs text-cream/50">{formatStamp(row.last_scan_at)}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/70">
                    ${(row.gift_total || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!ranked.length ? (
            <p className="py-8 text-sm text-cream/40" data-testid="harvest-accounts-empty">
              No season accounts yet. Import a CSV with company, tier and package_key to start.
            </p>
          ) : null}
        </div>
      </Panel>

      <AccountDrawer
        accountId={open}
        landingBase={settings?.landing_base}
        bookingUrl={settings?.booking_url}
        onClose={() => setOpen(null)}
        onChanged={load}
      />
    </HarvestShell>
  );
}
